import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import './BaseGamePage.css';

interface Number {
  number: number;
  status: 'active' | 'completed';
  completed_at?: string;
}

interface GameData {
  id: string;
  gameType: string;
  status: string;
  numbers: Number[];
}

export function BaseGamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const numbersPerPage = 50;

  useEffect(() => {
    loadGame();
    
    // Сохраняем текущую игру как последнюю
    if (gameId) {
      localStorage.setItem('last-game', JSON.stringify({ gameId, gameType: 'base' }));
      
      // Восстанавливаем сохраненную страницу для этой игры
      const savedPage = localStorage.getItem(`base-game-page-${gameId}`);
      if (savedPage) {
        const page = parseInt(savedPage, 10);
        if (page >= 1) {
          setCurrentPage(page);
        }
      }
    }
  }, [gameId]);

  const loadGame = async () => {
    try {
      const response = await api.get(`/games/${gameId}`);
      const gameData = response.data;
      
      // Убеждаемся, что номера загружены и отсортированы
      if (gameData.numbers && Array.isArray(gameData.numbers)) {
        // Сортируем номера по number для правильного отображения
        gameData.numbers.sort((a: Number, b: Number) => a.number - b.number);
        
        // Убеждаемся, что все номера присутствуют (001-999)
        if (gameData.numbers.length < 999) {
          console.warn(`Game ${gameId} has only ${gameData.numbers.length} numbers, expected 999`);
        }
        
        // Логируем для отладки
        const completedCount = gameData.numbers.filter((n: Number) => n.status === 'completed').length;
        console.log(`Loaded game ${gameId}: ${completedCount} completed numbers out of ${gameData.numbers.length}`);
        
        // Проверяем, что статусы правильные
        const activeNumbers = gameData.numbers.filter((n: Number) => n.status === 'active');
        const completedNumbers = gameData.numbers.filter((n: Number) => n.status === 'completed');
        console.log(`Active: ${activeNumbers.length}, Completed: ${completedNumbers.length}`);
      } else {
        console.error('No numbers array in game data:', gameData);
      }
      
      setGame(gameData);
    } catch (error) {
      console.error('Failed to load game:', error);
      alert('Ошибка загрузки игры');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleNumberClick = (number: number) => {
    if (game?.numbers.find((n) => n.number === number)?.status === 'active') {
      setSelectedNumber(number);
    }
  };

  const handleComplete = async () => {
    if (!selectedNumber) return;

    try {
      await api.post(`/games/${gameId}/base/complete`, { number: selectedNumber });
      setSelectedNumber(null);
      await loadGame();
    } catch (error: any) {
      // Обрабатываем офлайн-режим
      if (error.response?.data?.offline || error.isOffline) {
        alert('Запрос сохранен. Будет отправлен при восстановлении соединения.');
        // Обновляем локально для отображения
        if (game) {
          const updatedNumbers = game.numbers.map((num) =>
            num.number === selectedNumber
              ? { ...num, status: 'completed' as const, completed_at: new Date().toISOString() }
              : num
          );
          setGame({
            ...game,
            numbers: updatedNumbers,
          });
        }
        setSelectedNumber(null);
      } else {
        alert(error.response?.data?.error?.message || 'Ошибка');
      }
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (!game) {
    return <div>Игра не найдена</div>;
  }

  const numbers = game.numbers || [];
  const totalPages = Math.ceil(numbers.length / numbersPerPage);
  const startIndex = (currentPage - 1) * numbersPerPage;
  const endIndex = startIndex + numbersPerPage;
  const currentNumbers = numbers.slice(startIndex, endIndex);

  const completedCount = numbers.filter((n) => n.status === 'completed').length;
  const totalCount = numbers.length;

  return (
    <div className="base-game-page">
      <div className="game-header">
        <button className="back-button" onClick={() => {
          // Устанавливаем флаг, что пользователь явно вышел на главную
          localStorage.setItem('skip-auto-redirect', 'true');
          navigate('/');
        }}>
          ← Назад
        </button>
        <h1>Базовый</h1>
        <div className="progress">
          {completedCount} / {totalCount}
        </div>
      </div>

      <div className="numbers-grid">
        {currentNumbers.map((num) => (
          <button
            key={num.number}
            className={`number-button ${
              num.status === 'completed' ? 'completed' : 'active'
            }`}
            onClick={() => handleNumberClick(num.number)}
          >
            {String(num.number).padStart(3, '0')}
          </button>
        ))}
      </div>

      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            if (gameId) {
              localStorage.setItem(`base-game-page-${gameId}`, String(newPage));
            }
          }}
        >
          Предыдущая
        </button>
        <span>
          Страница {currentPage} из {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            if (gameId) {
              localStorage.setItem(`base-game-page-${gameId}`, String(newPage));
            }
          }}
        >
          Следующая
        </button>
      </div>

      {selectedNumber !== null && (
        <div className="modal-overlay" onClick={() => setSelectedNumber(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Погасить номер {String(selectedNumber).padStart(3, '0')}?</h2>
            <div className="modal-actions">
              <button className="confirm-button" onClick={handleComplete}>
                Погасить
              </button>
              <button
                className="cancel-button"
                onClick={() => setSelectedNumber(null)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

